import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/cn'
import { useSession } from '@/lib/session'
import { useAthletes, useActiveAssignments } from '@/hooks/useAthletes'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useTestingDue } from '@/hooks/useTestingDue'
import { SendNudge } from '@/components/SendNudge'
import { NewAthleteDialog } from '@/components/NewAthleteDialog'
import { Logo } from '@/components/Logo'
import { useState } from 'react'

const SOURCE_STYLES: Record<string, string> = {
  ai: 'bg-data/15 text-data border-data/30',
  manual: 'bg-muted/10 text-muted border-border-strong',
  vald: 'bg-success/15 text-success border-success/30',
  import: 'bg-gold/15 text-gold border-gold/30',
}

export default function CoachDashboard() {
  const navigate = useNavigate()

  const { signOut } = useSession()
  const qc = useQueryClient()
  const { data: everyone = [], isLoading, error } = useAthletes(true, { includeArchived: true })
  const athletes = everyone.filter((a) => !a.archived_at)
  const archived = everyone.filter((a) => a.archived_at)
  const { data: assignments = {} } = useActiveAssignments(true)
  const [showArchived, setShowArchived] = useState(false)
  const [restoring, setRestoring] = useState<string | null>(null)
  const unarchive = async (id: string) => {
    setRestoring(id)
    const { error: err } = await supabase.from('athletes').update({ archived_at: null }).eq('id', id)
    setRestoring(null)
    if (err) return alert('Could not unarchive: ' + err.message)
    await qc.invalidateQueries({ queryKey: ['athletes'] })
    await qc.invalidateQueries({ queryKey: ['testing-due'] })
  }
  const dueQ = useTestingDue(true)
  const [showDue, setShowDue] = useState(false)
  const [newAthlete, setNewAthlete] = useState(false)

  return (
    <div className="flex min-h-full flex-col">
      {/* Top bar */}
      <header className="sticky top-0 z-20 flex items-center gap-4 border-b border-gold/25 bg-surface/95 px-5 py-3 backdrop-blur">
        <Logo className="h-7" />
        <span className="rounded-full border border-border-strong px-2.5 py-0.5 font-condensed text-[11px] uppercase tracking-[0.2em] text-muted">
          Coach
        </span>
        <div className="ml-auto flex items-center gap-2">
          <SendNudge />
          <Button variant="secondary" size="sm" onClick={() => navigate('/gym')}>
            Gym View
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => {
              await signOut()
              navigate('/')
            }}
          >
            Sign out
          </Button>
        </div>
      </header>

      <main className="mx-auto w-full max-w-5xl flex-1 px-5 pb-16 pt-6">
        {/* Stat strip */}
        <div className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Athletes" value={isLoading ? '…' : String(athletes.length)} />
          <Stat label="Active Programs" value={isLoading ? '…' : String(athletes.filter((a) => assignments[a.id]).length)} />
          <Stat label="With VALD Link" value={isLoading ? '…' : String(athletes.filter((a) => a.vald_profile_id).length)} accent />
          <Stat label="VALD Tests" value="998" data />
        </div>

        {/* Who needs retesting */}
        {dueQ.data && dueQ.data.due.length > 0 && (
          <div className="mb-6 overflow-hidden rounded-card border border-gold/35 bg-gold/[.06]">
            <button
              onClick={() => setShowDue((v) => !v)}
              className="flex w-full items-center gap-3 px-4 py-3 text-left"
            >
              <span className="text-lg">⚠</span>
              <span className="flex-1">
                <span className="block font-condensed text-sm font-bold uppercase tracking-[0.12em] text-gold">
                  {dueQ.data.due.length} athletes due for testing
                </span>
                <span className="block text-xs text-muted">
                  {dueQ.data.overdue} overdue · {dueQ.data.neverTested} never tested ·{' '}
                  {dueQ.data.upToDate} up to date
                </span>
              </span>
              <span className={cn('text-gold transition-transform', showDue && 'rotate-180')}>▾</span>
            </button>

            {showDue && (
              <div className="max-h-80 overflow-y-auto border-t border-gold/25 animate-fade-in">
                {dueQ.data.due.map((a) => (
                  <button
                    key={a.athlete_id}
                    onClick={() => navigate(`/athlete/${a.athlete_id}`)}
                    className="flex w-full items-center gap-3 border-b border-border/60 px-4 py-2 text-left last:border-0 hover:bg-white/[.03]"
                  >
                    <span className="flex-1 truncate font-condensed font-bold">{a.name}</span>
                    <span
                      className={cn(
                        'font-condensed text-[11px] uppercase tracking-[0.1em]',
                        a.days == null ? 'text-subtle' : 'text-muted'
                      )}
                    >
                      {a.days == null ? 'never tested' : `${a.days}d ago`}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-2xl tracking-wide">Roster</h2>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => setNewAthlete(true)}>+ New Athlete</Button>
            <Button variant="secondary" size="sm" onClick={() => navigate('/coach/templates')}>📋 Templates</Button>
            <Button size="sm" onClick={() => navigate('/coach/builder')}>+ New Program</Button>
          </div>
        </div>

        {error && (
          <p className="mb-4 rounded-btn border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
            Couldn’t load athletes: {(error as Error).message}
          </p>
        )}

        {/* Roster table */}
        <div className="overflow-hidden rounded-card border border-border bg-surface">
          <div className="grid grid-cols-[1fr_auto_auto] items-center gap-4 border-b border-border bg-white/[.02] px-4 py-2.5 font-condensed text-[11px] uppercase tracking-[0.2em] text-muted sm:grid-cols-[1.4fr_1.6fr_auto_auto]">
            <span>Athlete</span>
            <span className="hidden sm:block">Program</span>
            <span className="text-center">Week</span>
            <span className="text-right">VALD</span>
          </div>

          {isLoading && (
            <div className="space-y-px">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-14 animate-pulse bg-surface-2/50" />
              ))}
            </div>
          )}

          {!isLoading &&
            athletes.map((a) => (
              <button
                key={a.id}
                onClick={() => navigate(`/athlete/${a.id}`)}
                className="grid w-full grid-cols-[1fr_auto_auto] items-center gap-4 border-b border-border px-4 py-3.5 text-left transition-colors last:border-0 hover:bg-surface-2 sm:grid-cols-[1.4fr_1.6fr_auto_auto]"
              >
                <div className="min-w-0">
                  <p className="font-condensed text-lg font-bold leading-tight">{a.name}</p>
                  <p className="truncate text-xs text-muted sm:hidden">{assignments[a.id]?.programName ?? 'No program'}</p>
                </div>
                <p className="hidden truncate text-sm text-subtle sm:block">{assignments[a.id]?.programName ?? 'No program'}</p>
                <span className="text-center font-condensed text-lg font-bold text-gold">
                  {assignments[a.id]?.currentWeek ?? '—'}
                </span>
                <span className="flex justify-end">
                  <span
                    className={cn(
                      'rounded-full border px-2.5 py-0.5 font-condensed text-[11px] uppercase tracking-wide',
                      a.vald_profile_id ? SOURCE_STYLES.vald : SOURCE_STYLES.manual
                    )}
                  >
                    {a.vald_profile_id ? 'linked' : '—'}
                  </span>
                </span>
              </button>
            ))}
        </div>

        {archived.length > 0 && (
          <div className="mt-4">
            <button
              onClick={() => setShowArchived((v) => !v)}
              className="font-condensed text-xs uppercase tracking-[0.15em] text-muted hover:text-gold"
            >
              {showArchived ? '▾' : '▸'} Archived athletes ({archived.length})
            </button>
            {showArchived && (
              <div className="mt-2 overflow-hidden rounded-card border border-border bg-surface/60 animate-fade-in">
                {archived.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 border-b border-border px-4 py-2.5 last:border-0">
                    <button
                      onClick={() => navigate(`/athlete/${a.id}`)}
                      className="min-w-0 flex-1 truncate text-left font-condensed text-base font-bold text-muted hover:text-text"
                    >
                      {a.name}
                    </button>
                    <span className="text-[11px] text-subtle">
                      archived {new Date(a.archived_at!).toLocaleDateString()}
                    </span>
                    <Button size="sm" variant="secondary" disabled={restoring === a.id} onClick={() => unarchive(a.id)}>
                      {restoring === a.id ? 'Restoring…' : 'Unarchive'}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {newAthlete && <NewAthleteDialog onClose={() => setNewAthlete(false)} />}
    </div>
  )
}

function Stat({
  label,
  value,
  accent,
  data,
}: {
  label: string
  value: string
  accent?: boolean
  data?: boolean
}) {
  return (
    <div className="rounded-card border border-border bg-gradient-to-b from-surface-2 to-surface p-4">
      <p
        className={cn(
          'font-display text-4xl leading-none',
          accent ? 'text-gold-gradient' : data ? 'text-data' : 'text-text'
        )}
      >
        {value}
      </p>
      <p className="mt-1 font-condensed text-[11px] uppercase tracking-[0.2em] text-muted">{label}</p>
    </div>
  )
}
